import React, { ChangeEvent, Component, KeyboardEvent } from "react";
import { observable } from "mobx";
import { observer } from "mobx-react";

import { assertUnreachable } from "@formant/core/common/assertUnreachable";
import { capitalize } from "@formant/core/common/capitalize";
import { HardwareType } from "@formant/core/model/HardwareType";
import { hardwareTypes } from "@formant/core/model/hardwareTypes";
import { IDeviceConfigurationDocument } from "@formant/core/model/IDeviceConfigurationDocument";
import { IDeviceTeleopCustomStreamConfiguration } from "@formant/core/model/IDeviceTeleopCustomStreamConfiguration";
import { IDeviceTeleopHardwareStreamConfiguration } from "@formant/core/model/IDeviceTeleopHardwareStreamConfiguration";
import { IDeviceTeleopRosStreamConfiguration } from "@formant/core/model/IDeviceTeleopRosStreamConfiguration";
import { IRosTopic } from "@formant/core/model/IRosTopic";
import { IValidation } from "@formant/core/model/IValidation";
import {
    rosTypeBool,
    rosTypeCompressedImage,
    rosTypeGoalID,
    rosTypePoseStamped,
    rosTypeTwist,
    rosTypeVideoFrame,
} from "@formant/core/model/rosTypes";
import { RosVersion } from "@formant/core/model/RosVersion";
import {
    defaultROSVersion,
    rosVersion1,
    rosVersions,
} from "@formant/core/model/rosVersions";
import { RtcStreamType } from "@formant/core/model/RtcStreamType";
import {
    rtcAudioChunkStreamType,
    rtcBitsetStreamType,
    rtcCompressedImageStreamType,
    rtcH264VideoFrameStreamType,
} from "@formant/core/model/rtcStreamTypes";
import { TeleopMode } from "@formant/core/model/TeleopMode";
import { TeleopRosTopicType } from "@formant/core/model/TeleopRosTopicType";
import { teleopRosTopicTypes } from "@formant/core/model/teleopRosTopicTypes";
import { videoQualities } from "@formant/core/model/videoQualities";
import { VideoQuality } from "@formant/core/model/VideoQuality";
import { InputElement } from "@formant/ui/component/common/Field";
import { HStack } from "@formant/ui/component/common/HStack";
import { IconName } from "@formant/ui/component/common/Icon";
import { TextField } from "@formant/ui/component/common/TextField";
import { VStack } from "@formant/ui/component/common/VStack";

import { cleanHardwareName } from "../../utils/cleanHardwareName";
import { AddConnectionModal } from "../common/AddConnectionModal";
import { Heading2 } from "../common/Heading2";
import { Help } from "../common/Help";
import { InputButton } from "../common/InputButton";
import { NoDataMessage } from "../common/NoDataMessage";
import { NumberField } from "../common/NumberField";
import { Popup } from "../common/Popup";
import { SelectField } from "../common/SelectField";
import { StandardButton } from "../common/StandardButton";
import { Switch } from "../common/Switch";
import { ItemList } from "../ItemList";
import { IItem } from "../ItemList/IItem";
import { AddButton } from "./AddButton";
import * as styles from "./TeleopStreamConfigurationGroup.scss";

const normalizeRosType = (type: string) => type.replace("/msg/", "/");
const convertTopicToLabel = (topic: string) => {
    return topic
        .replace(/^\//, "") // remove leading slash
        .replace(/_/g, " ") // replace underscores with spaces
        .replace(/\b\w/g, l => l.toUpperCase()) // capitalize first letter of each word
        .replace(/([a-z](?=[A-Z]))/g, "$1 "); // break up camel case
};

export interface ITeleopStreamConfigurationGroupProps {
    label: string;
    topics: IRosTopic[];
    rosInputTypes: string[];
    customInputType: RtcStreamType;
    rosOutputTypes: TeleopRosTopicType[];
    mode: TeleopMode;
    document: IDeviceConfigurationDocument;
    realtimeMode?: boolean;
    addStreamCopy?: string;
    help?: string;
    itemName?: string;
    max?: number;
    disableCustom?: boolean;
    enableHardware?: boolean;
    enableROS?: boolean;
    hardwarePathsMap?: { [id: string]: string };
    nestedHardwarePathsMap?: { [id: string]: { [id: string]: string } };
    onEdit?: () => void;
    specificOnEdit?: (name: string, sourceType: string) => () => void;
    editableTypes?: string[];
    onAddRosStream: (stream: IDeviceTeleopRosStreamConfiguration) => void;
    onAddCustomStream: (stream: IDeviceTeleopCustomStreamConfiguration) => void;
    onAddHardwareStream: (
        stream: IDeviceTeleopHardwareStreamConfiguration,
    ) => void;
    onDeleteStream: (
        streamName: string,
        mode: TeleopMode,
        label?: string,
    ) => void;
    onCreatePreChildren?: (
        source: string,
        name: string,
        type: string,
    ) => React.ReactNode;
}

interface IErrorState {
    errorMessage: string;
}

function stringToOutputType(name: string): TeleopRosTopicType | undefined {
    return (
        teleopRosTopicTypes.map(_ => _).find(_ => name === _) ||
        (name === "sensor_msgs/Image" ? rosTypeVideoFrame : undefined)
    );
}

function iconFromType(type?: TeleopRosTopicType): IconName | undefined {
    switch (type) {
        case rosTypeVideoFrame:
            return "video";
        case rosTypeCompressedImage:
            return "image";
        case rosTypeBool:
            return "bitset";
        case rosTypeTwist:
            return "teleop";
        case rosTypeGoalID:
            return "stop";
        case rosTypePoseStamped:
            return "goal";
        default:
            return undefined;
    }
}

type StreamType =
    | {
          type: "ros";
          rosVersion?: RosVersion;
          label?: string;
          topicValue?: string;
          topicType?: TeleopRosTopicType;
          encodeVideo?: boolean;
          quality?: VideoQuality;
          buttonLabel?: string;
          bitrate?: number;
          disableAdaptiveQuality?: boolean;
          overlayClock?: boolean;
      }
    | {
          type: "custom";
          name?: string;
          label?: string;
          encodeVideo?: boolean;
          quality?: VideoQuality;
          bitrate?: number;
          disableAdaptiveQuality?: boolean;
          overlayClock?: boolean;
      }
    | {
          type: "hardware";
          name?: string;
          label?: string;
          hwDescriptor?: string;
          hardwareType: HardwareType;
          rtspEncodingNeeded?: boolean;
          isOnvif?: boolean;
          ipCamUsername?: string;
          ipCamPassword?: string;
          quality: VideoQuality;
          bitrate?: number;
          disableAdaptiveQuality?: boolean;
          overlayClock?: boolean;
      }
    | undefined;

@observer
export class TeleopStreamConfigurationGroup extends Component<ITeleopStreamConfigurationGroupProps> {
    @observable
    private stream: StreamType;

    @observable
    private editing = false;

    @observable
    private addV2 = false;

    @observable
    private addConnectionPosition = { top: 0, left: 0 };

    @observable
    private validation: IValidation | undefined;

    private existingStreamNames: string[] = [];

    // eslint-disable-next-line react/state-in-constructor
    state: IErrorState = {
        errorMessage: "",
    };

    public render() {
        const {
            customInputType,
            rosInputTypes,
            rosOutputTypes,
            label,
            help,
            document,
            realtimeMode,
            mode,
            disableCustom,
            enableHardware,
            enableROS,
            max,
            hardwarePathsMap,
            nestedHardwarePathsMap,
            editableTypes,
            onEdit,
            specificOnEdit,
            topics: topicList,
            addStreamCopy,
            onCreatePreChildren,
            itemName,
        } = this.props;
        const { stream } = this;
        if (rosOutputTypes.length === 0 && disableCustom && !enableHardware) {
            return false;
        }

        const configuredRosStreams = (
            realtimeMode ? document.realtime : document.teleop
        )?.rosStreams;
        const configuredTopics: IRosTopic[] = (configuredRosStreams || [])
            .filter(
                _ =>
                    _.mode === mode &&
                    rosInputTypes.includes(normalizeRosType(_.topicType)),
            )
            .map(_ => ({
                name: _.topicName,
                type: _.topicType,
                label: _.label,
                rosVersion: _.rosVersion,
            }));

        const topics = configuredTopics
            .sort((a, b) =>
                a.name.toLowerCase().localeCompare(b.name.toLowerCase()),
            )
            .map(_ => ({
                ..._,
                source: "ros",
                labels: undefined,
            }));

        const availableTopics = topicList
            .filter(
                ({ name, type }) =>
                    rosInputTypes.includes(normalizeRosType(type)) &&
                    !configuredTopics.some(_ => _.name === name),
            )
            .map(_ => _.name);

        const configuredCustomStreams = (
            (realtimeMode ? document.realtime : document.teleop)
                ?.customStreams || []
        )
            .filter(_ => _.mode === mode && customInputType === _.rtcStreamType)
            .sort((a, b) =>
                a.name.toLowerCase().localeCompare(b.name.toLowerCase()),
            )
            .map(_ => ({
                source: "custom",
                name: _.name,
                type: _.rtcStreamType,
                labels: _.labels,
                label: _.label,
            }));

        const configuredHardwareStreams = (
            (realtimeMode ? document.realtime : document.teleop)
                ?.hardwareStreams || []
        )
            .filter(_ => _.mode === mode && customInputType === _.rtcStreamType)
            .sort((a, b) =>
                a.name.toLowerCase().localeCompare(b.name.toLowerCase()),
            )
            .map(_ => ({
                source: "hardware",
                name: _.name,
                type: _.rtcStreamType,
                labels: undefined,
                label: _.label,
            }));

        this.existingStreamNames = [
            ...configuredTopics,
            ...configuredCustomStreams,
            ...configuredHardwareStreams,
        ]
            .map(_ => _.label)
            .filter(label => label !== undefined);

        const isButtonConfig =
            customInputType === "bitset" && mode === "command";

        const items: IItem[] = [];
        [
            ...topics,
            ...configuredCustomStreams,
            ...configuredHardwareStreams,
        ].forEach(({ source, name, type, labels, label: streamLabel }) => {
            const itemEdit = specificOnEdit
                ? specificOnEdit(name, source)
                : onEdit || undefined;

            const canEdit =
                (editableTypes || []).includes(type) ||
                ((source === "hardware" || source === "custom") &&
                    type === "h264-video-frame");

            (labels || [name]).forEach(_ => {
                items.push({
                    key: _,
                    label: streamLabel || _,
                    type:
                        source === "ros"
                            ? "ROS"
                            : source === "custom"
                              ? "API"
                              : "Hardware",
                    icon: iconFromType(stringToOutputType(type)),
                    checked: false,
                    disabled: false,
                    onToggle: () => {},
                    preChildren:
                        onCreatePreChildren &&
                        onCreatePreChildren(source, name, type),
                    onEdit: canEdit ? itemEdit : undefined,
                    onDelete: this.onDelete(
                        name,
                        labels?.length ? _ : undefined,
                    ),
                });
            });
        });

        const suggestions: IItem[] = (
            topicList.filter(
                ({ name, type }) =>
                    rosInputTypes.includes(type) &&
                    !(configuredRosStreams || []).some(
                        _ => _.topicName === name,
                    ),
            ) || []
        ).map(({ name, type, rosVersion }) => {
            return {
                key: name,
                label: name,
                icon: iconFromType(stringToOutputType(type)),
                checked: false,
                disabled: false,
                onToggle: this.onAddSuggestion,
                rosVersion: rosVersion || defaultROSVersion,
            };
        });

        const topicValue =
            stream?.type === "ros" ? stream.topicValue : undefined;
        const topicInvalid =
            (topicValue || "").length > 0 &&
            (topics.some(_ => _.name === topicValue) ||
                !(topicValue || "").includes("/"));

        const isImage =
            (stream?.type === "ros" &&
                stream.topicType === rosTypeCompressedImage) ||
            (stream?.type === "custom" &&
                (customInputType === rtcCompressedImageStreamType ||
                    customInputType === rtcH264VideoFrameStreamType));
        const isIP =
            stream?.type === "hardware" && stream.hardwareType === "ip";

        const bitrateField = (
            <HStack vAlign="center" hAlign="start" innerSpacing="sm">
                <NumberField
                    label="Bitrate"
                    numberValue={stream?.bitrate}
                    allowUndefined={true}
                    invalid={!!this.validation?.bitrate}
                    onChangeNumber={this.onChangeBitrate}
                />
                <Help
                    text="Set your desired bitrate in kbps. If left blank, the agent will choose the best bitrate for the preferred quality selected above. If specified, the agent will be forced to use this bitrate. Click for docs."
                    to="https://docs.formant.io/docs/getting-started-add-a-teleoperation-image-stream"
                />
            </HStack>
        );

        const disableAdaptiveQualitySwitch = (
            <Switch
                state={stream?.disableAdaptiveQuality ?? false}
                onChange={this.onToggleDisableAdaptiveQuality}
                label="Disable Adaptive Quality"
                stateLabels={["No", "Yes"]}
                info="If set to No, the Formant agent will be able to reduce video quality in poor network connection to maintain video stream. If set to Yes, the agent will not be allowed to change the video quality."
            />
        );

        const advancedSettingsHeader = (
            <HStack vAlign="center" hAlign="space-between">
                <div>Advanced Settings</div>
                <Help
                    text="If you also configure your video settings via environment variables, the environment variables will override what you configure here. Click for docs."
                    to="https://docs.formant.io/docs/getting-started-add-a-teleoperation-image-stream"
                />
            </HStack>
        );

        const clockSwitch = (
            <Switch
                state={stream?.overlayClock ?? false}
                onChange={this.onToggleOverlayClock}
                label="Clock Overlay"
                stateLabels={["No", "Yes"]}
                info="Overlay the video stream with a clock."
            />
        );

        const noStreamContent = stream === undefined && (
            <>
                <HStack
                    vAlign="top"
                    hAlign="start"
                    innerSpacing="sm"
                    className={styles.buttons}
                >
                    {rosInputTypes.length > 0 && (
                        <InputButton
                            onClicked={this.enableRosMode}
                            text="Add ROS topic"
                            mainIcon="ros"
                            actionIcon="plus"
                            data-testid="add-ros-topic-button"
                        />
                    )}
                    {!disableCustom && (
                        <InputButton
                            onClicked={this.enableCustomMode}
                            text="Add from API"
                            mainIcon="code"
                            actionIcon="plus"
                            data-testid="add-from-api-button"
                        />
                    )}
                    {enableHardware && (
                        <InputButton
                            onClicked={this.enableHardwareMode}
                            text="Add from Hardware"
                            mainIcon="hardware"
                            actionIcon="plus"
                            data-testid="add-from-hardware-button"
                        />
                    )}
                </HStack>

                {suggestions.length > 0 && (
                    <VStack innerSpacing="sm">
                        <p className={styles.text}>
                            Here are some compatible ROS topics that we detected
                            on your device.
                        </p>
                        <ItemList
                            items={suggestions}
                            className={styles.suggestions}
                        />
                        <StandardButton
                            onClicked={() =>
                                this.onAddAllSuggestions(suggestions)
                            }
                            data-testid="add-all-button"
                        >
                            Add All
                        </StandardButton>
                    </VStack>
                )}
            </>
        );
        const rosContent = stream?.type === "ros" && (
            <>
                <div className={styles.type}>
                    {rosOutputTypes
                        .map(_ =>
                            _ === "formant/H264VideoFrame"
                                ? "sensor_msgs/Image"
                                : _,
                        )
                        .join(", ")}
                </div>
                <VStack innerSpacing="md">
                    {realtimeMode && (
                        <TextField
                            value={stream.label || ""}
                            onChange={this.onChangeLabelRos}
                            onKeyDown={this.onKeydown}
                            label="Connection"
                            placeholder="Connection"
                            autoFocus={true}
                            small={true}
                        />
                    )}
                    {this.state.errorMessage && (
                        <div style={{ color: "red" }}>
                            {this.state.errorMessage}
                        </div>
                    )}
                    <TextField
                        value={stream.topicValue || ""}
                        onChange={this.onChangeNewTopic}
                        onKeyDown={this.onKeydown}
                        label="Topic name"
                        placeholder="Topic name - eg. /camera/front"
                        list={availableTopics}
                        autoFocus={false}
                        invalid={topicInvalid}
                        small={true}
                    />
                    {isButtonConfig ? (
                        <TextField
                            value={stream.buttonLabel || ""}
                            onChange={this.onChangeButtonLabelRos}
                            onKeyDown={this.onKeydown}
                            label="Button label"
                            placeholder="Label for your button"
                            autoFocus={false}
                            small={true}
                        />
                    ) : null}
                    {rosOutputTypes.length > 1 && stream.topicType && (
                        <SelectField
                            label="Topic type"
                            small={true}
                            value={stream.topicType}
                            onChange={this.onChangeTopicType}
                        >
                            {rosOutputTypes.map(_ => (
                                <option key={_} value={_}>
                                    {_ === "formant/H264VideoFrame"
                                        ? "sensor_msgs/Image"
                                        : _}
                                </option>
                            ))}
                        </SelectField>
                    )}
                    {stream !== undefined && stream.type === "ros" && (
                        <>
                            <p>
                                ROS version
                                <Help text="The ROS version of the topic." />
                            </p>
                            <SelectField
                                small={true}
                                label="Ros Version"
                                value={stream.rosVersion}
                                onChange={this.onChangeNewRosVersion}
                            >
                                {rosVersions.map(_ => (
                                    <option key={_} value={_}>
                                        {this.getRosVersionLabel(_)}
                                    </option>
                                ))}
                            </SelectField>
                        </>
                    )}
                </VStack>
            </>
        );

        const hardwareContent = stream?.type === "hardware" && (
            <VStack innerSpacing="md">
                {realtimeMode && (
                    <TextField
                        value={stream?.label || ""}
                        onChange={this.onChangeHardwareLabel}
                        onKeyDown={this.onKeydown}
                        label="Connection"
                        placeholder="Connection"
                        autoFocus={true}
                        small={true}
                    />
                )}
                {this.state.errorMessage && (
                    <div style={{ color: "red" }}>
                        {this.state.errorMessage}
                    </div>
                )}
                <SelectField
                    small={true}
                    label="Hardware type"
                    value={stream?.hardwareType}
                    onChange={this.onChangeHardwareType}
                >
                    {hardwareTypes.map(_ => (
                        <option key={_} value={_}>
                            {_}
                        </option>
                    ))}
                </SelectField>
                <VStack innerSpacing="sm">
                    <HStack
                        innerSpacing="md"
                        hAlign="space-between"
                        vAlign="center"
                    >
                        <div className={styles.label}>
                            {isIP
                                ? "RTSP URI of IP camera"
                                : "Hardware path to USB camera"}
                        </div>

                        <Help
                            text={
                                isIP
                                    ? "RTSP URI of IP camera e.g. 'admin:123456@192.168.1.110:554/h264'"
                                    : "Hardware path to USB camera e.g. '/dev/video0'"
                            }
                        />
                    </HStack>
                    <TextField
                        value={stream.hwDescriptor}
                        onChange={this.onChangeNewHardwareStreamDevice}
                        onKeyDown={this.onKeydown}
                        label="Hardware"
                        placeholder={
                            customInputType === rtcAudioChunkStreamType
                                ? "Audio hardware name"
                                : customInputType ===
                                    rtcH264VideoFrameStreamType
                                  ? isIP
                                      ? "Video RTSP URI e.g. 'rtsp://192.168.1.110:554/h264'"
                                      : "Hardware path e.g. '/dev/video0'"
                                  : ""
                        }
                        autoFocus={true}
                        small={true}
                        list={Object.keys(
                            hardwarePathsMap ??
                                nestedHardwarePathsMap?.[stream.hardwareType] ??
                                {},
                        )}
                    />
                </VStack>
                {isIP && (
                    <>
                        <Switch
                            state={stream?.rtspEncodingNeeded ?? false}
                            onChange={this.onChangeRTSPEncodingNeeded}
                            label="Encoding required"
                            stateLabels={["No", "Yes"]}
                            info="If the IP camera source is outputting h264, encoding should be disabled"
                        />
                        <Switch
                            state={stream?.isOnvif ?? false}
                            onChange={this.onChangeIsOnvif}
                            label="Supports ONVIF"
                            stateLabels={["No", "Yes"]}
                            info="If the camera supports ONVIF protocol"
                        />
                        <TextField
                            value={stream.ipCamUsername}
                            onChange={this.onChangeIpCamUsername}
                            label="Username"
                            placeholder="Username e.g. admin"
                            small={true}
                        />
                        <TextField
                            value={stream.ipCamPassword}
                            onChange={this.onChangeIpCamPassword}
                            label="Password"
                            placeholder="Password e.g. 123456"
                            small={true}
                        />
                    </>
                )}
            </VStack>
        );

        const customContent = stream?.type === "custom" && (
            <>
                {realtimeMode && (
                    <TextField
                        value={stream.label || ""}
                        onChange={this.onChangeCustomLabel}
                        onKeyDown={this.onKeydown}
                        label="Connection"
                        placeholder="Connection"
                        autoFocus={true}
                        small={true}
                    />
                )}
                {this.state.errorMessage && (
                    <div style={{ color: "red" }}>
                        {this.state.errorMessage}
                    </div>
                )}
                {customInputType === rtcBitsetStreamType && !realtimeMode && (
                    <div>
                        {mode === "command"
                            ? 'Bitsets will be sent on the "Buttons" stream'
                            : 'Bitsets must be sent to the "Status" stream'}
                    </div>
                )}
                <TextField
                    value={
                        realtimeMode
                            ? stream.name || ""
                            : stream.label !== undefined
                              ? stream.label
                              : stream.name
                    }
                    onChange={this.onChangeNewCustomStream}
                    onKeyDown={this.onKeydown}
                    label="Name"
                    placeholder={
                        customInputType === rtcBitsetStreamType
                            ? "Bitset key name"
                            : `${capitalize(
                                  isImage ? "image" : customInputType,
                              )} stream name`
                    }
                    autoFocus={false}
                    small={true}
                    data-testid="bitset-key-name-input"
                />
            </>
        );
        return (
            <VStack innerSpacing="sm" className={styles["teleop-stream-group"]}>
                <HStack innerSpacing="sm" vAlign="center">
                    <AddButton
                        label={label}
                        minimal={realtimeMode}
                        returnMouseEventByDefault={true}
                        onClicked={
                            realtimeMode ? this.onAddV2 : this.onStartEdit
                        }
                        tooltips={
                            realtimeMode
                                ? undefined
                                : [
                                      "Add a new source",
                                      `Limit reached for this stream type. Maximum: ${max}`,
                                  ]
                        }
                        count={items.length}
                        max={realtimeMode ? undefined : max || 0}
                        data-testid={`teleop-configure-stream-button-${label}`}
                    />
                    {help && <Help text={help} />}
                </HStack>
                {items.length === 0 && (
                    <NoDataMessage asBanner={true}>
                        {`No ${label.toLocaleLowerCase()} ${
                            itemName || "streams"
                        } configured`}
                    </NoDataMessage>
                )}
                {this.addV2 && (
                    <AddConnectionModal
                        position={this.addConnectionPosition}
                        onClose={this.closeModal}
                        enableHardware={enableHardware || false}
                        enableROS={enableROS || false}
                        onAPIClicked={() => {
                            this.enableCustomMode();
                            this.editing = true;
                        }}
                        onHardwareClicked={() => {
                            this.enableHardwareMode();
                            this.editing = true;
                        }}
                        onRosClicked={() => {
                            this.enableRosMode();
                            this.editing = true;
                        }}
                    />
                )}
                <Popup
                    visible={this.editing}
                    variant="light"
                    hasClosed={this.hasClosed}
                    onClose={this.onStopEdit}
                    windowClassName={styles.popup}
                >
                    <VStack innerSpacing="md">
                        <Heading2>
                            {stream?.type === "ros"
                                ? "Add a new ROS topic"
                                : stream?.type === "hardware"
                                  ? "Use connected hardware"
                                  : stream?.type === "custom"
                                    ? "Add from API"
                                    : addStreamCopy ||
                                      "Add a teleoperation stream"}
                        </Heading2>
                        {noStreamContent}
                        {rosContent}
                        {hardwareContent}
                        {customContent}
                        {stream !== undefined &&
                            stream.type !== "hardware" &&
                            isImage && (
                                <Switch
                                    state={stream.encodeVideo || false}
                                    label="Video encoding"
                                    onChange={this.onToggleImageEncoding}
                                    info="If YES, image stream will be sent through H264 encoder. If OFF, image stream will be passed directly to front end. If sending pre-encoded video, set this to YES."
                                    stateLabels={["off", "on"]}
                                />
                            )}
                        {((stream?.type !== "hardware" &&
                            (stream?.encodeVideo ||
                                (stream?.type === "ros" &&
                                    stream?.topicType ===
                                        "formant/H264VideoFrame"))) ||
                            (stream?.type === "hardware" &&
                                !(
                                    stream.hardwareType === "ip" &&
                                    stream.rtspEncodingNeeded === false
                                ) &&
                                customInputType ===
                                    rtcH264VideoFrameStreamType)) && (
                            <VStack innerSpacing="sm">
                                <HStack
                                    innerSpacing="md"
                                    hAlign="space-between"
                                >
                                    <div className={styles.label}>
                                        Video quality
                                    </div>
                                    <Help
                                        text="Preferred video quality. If this video source is also configured for telemetry, the lower of the two qualities will be used."
                                        to="https://docs.formant.io/docs/getting-started-add-a-teleoperation-image-stream"
                                    />
                                </HStack>
                                <VStack innerSpacing="md">
                                    <SelectField
                                        small={true}
                                        label="Preferred video quality"
                                        value={stream.quality}
                                        onChange={this.onChangeNewStreamQuality}
                                    >
                                        {videoQualities.map(_ => (
                                            <option key={_} value={_}>
                                                {_}
                                            </option>
                                        ))}
                                    </SelectField>
                                    {advancedSettingsHeader}
                                    {bitrateField}
                                    {disableAdaptiveQualitySwitch}
                                    {clockSwitch}
                                </VStack>
                            </VStack>
                        )}
                        <HStack vAlign="center" hAlign="end">
                            <StandardButton
                                onClicked={this.onAddNewStream}
                                data-testid="done-button"
                            >
                                Done
                            </StandardButton>
                        </HStack>
                    </VStack>
                </Popup>
                <ItemList
                    items={items}
                    noIcons={realtimeMode}
                    minimalUX={realtimeMode}
                />
            </VStack>
        );
    }

    private onToggleImageEncoding = (encodeVideo: boolean) => {
        if (!this.stream || this.stream.type === "hardware") {
            return;
        }
        this.stream.encodeVideo = encodeVideo;
        this.stream.quality = "720p";
    };

    private enableRosMode = () => {
        this.stream = {
            type: "ros",
            rosVersion: rosVersion1,
            topicType: this.props.rosOutputTypes[0],
        };
    };

    private enableCustomMode = () => {
        const { customInputType, mode, realtimeMode } = this.props;
        const name =
            customInputType === rtcBitsetStreamType && !realtimeMode
                ? mode === "command"
                    ? "Buttons"
                    : "Status"
                : "";

        this.stream = {
            type: "custom",
            name,
            label:
                customInputType === rtcBitsetStreamType && !realtimeMode
                    ? ""
                    : undefined,
        };
    };
    private enableHardwareMode = () => {
        const name = "";
        const hwDescriptor = "";
        const quality = "720p";
        this.stream = {
            type: "hardware",
            name,
            hwDescriptor,
            quality,
            rtspEncodingNeeded: false,
            isOnvif: false,
            hardwareType: "usb",
        };
    };

    private onAddSuggestion = (label: string) => {
        const suggestionType = this.props.topics.find(
            _ => _.name === label,
        )?.type;

        if (!suggestionType) {
            return;
        }

        const topic = this.props.topics.find(_ => _.name === label);
        if (!topic) {
            return;
        }
        this.stream = {
            type: "ros",
            rosVersion: topic.rosVersion || defaultROSVersion,
            topicValue: label,
            topicType: stringToOutputType(suggestionType),
            label: convertTopicToLabel(label),
        };

        if (suggestionType) {
            this.onAddNewStream(this.stream);
        }
    };

    private onAddAllSuggestions = (suggestions: IItem[]) => {
        let inc: number = 0;
        for (const suggestion of suggestions) {
            setTimeout(
                () => {
                    this.onAddSuggestion(suggestion.label);
                },
                (inc += 10),
            );
            inc++;
        }
    };

    private onDelete = (streamName: string, label?: string) => () => {
        const { mode, onDeleteStream } = this.props;
        onDeleteStream(streamName, mode, label);
    };

    private onChangeBitrate = (newValue: number | undefined) => {
        if (this?.stream) {
            if (newValue !== undefined && newValue < 1) {
                this.validation = {
                    bitrate: ["Bitrate must be positive"],
                };
            } else {
                this.validation = undefined;
                this.stream.bitrate = newValue;
            }
        }
    };
    private onToggleDisableAdaptiveQuality = (newValue: boolean) => {
        if (this?.stream) {
            this.stream.disableAdaptiveQuality = newValue;
        }
    };
    private onToggleOverlayClock = (newValue: boolean) => {
        if (this?.stream) {
            this.stream.overlayClock = newValue;
        }
    };
    private onChangeNewTopic = (event: ChangeEvent<InputElement>) => {
        const existing = this.stream?.type === "ros" ? this.stream : {};
        this.stream = {
            ...existing,
            type: "ros",
            rosVersion: rosVersion1,
            topicValue: event.target.value,
        };
    };

    private onChangeButtonLabelRos = (event: ChangeEvent<InputElement>) => {
        const existing = this.stream?.type === "ros" ? this.stream : {};
        this.stream = {
            ...existing,
            type: "ros",
            rosVersion: rosVersion1,
            buttonLabel: event.target.value,
        };
    };

    private onChangeLabelRos = (event: ChangeEvent<InputElement>) => {
        const existing = this.stream?.type === "ros" ? this.stream : {};
        this.stream = {
            ...existing,
            type: "ros",
            rosVersion: rosVersion1,
            label: event.target.value,
        };
    };

    private onChangeTopicType = (event: ChangeEvent<InputElement>) => {
        const existing = this.stream?.type === "ros" ? this.stream : {};
        this.stream = {
            ...existing,
            type: "ros",
            rosVersion: rosVersion1,
            topicType: stringToOutputType(event.target.value),
        };
    };

    private onChangeNewCustomStream = (event: ChangeEvent<InputElement>) => {
        const { customInputType, realtimeMode } = this.props;
        const existing = this.stream?.type === "custom" ? this.stream : {};
        if (customInputType === rtcBitsetStreamType && !realtimeMode) {
            this.stream = {
                ...existing,
                type: "custom",
                label: event.target.value,
            };
        } else if (realtimeMode) {
            this.stream = {
                ...existing,
                type: "custom",
                name: event.target.value,
            };
        } else {
            this.stream = {
                ...existing,
                type: "custom",
                name: event.target.value,
            };
        }
    };
    private onChangeCustomLabel = (event: ChangeEvent<InputElement>) => {
        const { realtimeMode } = this.props;
        if (realtimeMode && this.stream?.type === "custom") {
            this.stream = { ...this.stream, label: event.target.value };
        }
    };

    private onChangeNewStreamQuality = (
        event: ChangeEvent<HTMLSelectElement>,
    ) => {
        const quality = event.target.value as VideoQuality;
        if (this.stream?.type) {
            this.stream = { ...this.stream, quality };
        }
    };

    private onChangeNewRosVersion = (event: ChangeEvent<HTMLSelectElement>) => {
        const rosVersion = event.target.value as RosVersion;
        if (this.stream?.type === "ros") {
            this.stream = { ...this.stream, rosVersion };
        }
    };

    private onChangeNewHardwareStreamDevice = (
        event: ChangeEvent<InputElement>,
    ) => {
        if (this.stream?.type === "hardware") {
            const name = event.target.value;
            let hwDescriptor = event.target.value;
            if (this.props.hardwarePathsMap !== undefined) {
                if (this.props.hardwarePathsMap[name] !== undefined) {
                    hwDescriptor = this.props.hardwarePathsMap[name];
                }
            }
            if (this.props.nestedHardwarePathsMap !== undefined) {
                if (
                    this.props.nestedHardwarePathsMap[this.stream.hardwareType][
                        name
                    ] !== undefined
                ) {
                    hwDescriptor =
                        this.props.nestedHardwarePathsMap[
                            this.stream.hardwareType
                        ][name];
                    this.onChangeIsOnvif(true);
                }
            }
            if (this.stream?.type === "hardware") {
                this.stream = {
                    ...this.stream,
                    hwDescriptor,
                    name,
                };
            }
        }
    };
    private onChangeRTSPEncodingNeeded = (newValue: boolean) => {
        if (this.stream?.type === "hardware") {
            this.stream = { ...this.stream, rtspEncodingNeeded: newValue };
        }
    };
    private onChangeIsOnvif = (newValue: boolean) => {
        if (this.stream?.type === "hardware") {
            this.stream = { ...this.stream, isOnvif: newValue };
        }
    };
    private onChangeIpCamUsername = (event: ChangeEvent<InputElement>) => {
        if (this.stream?.type === "hardware") {
            this.stream = { ...this.stream, ipCamUsername: event.target.value };
        }
    };
    private onChangeIpCamPassword = (event: ChangeEvent<InputElement>) => {
        if (this.stream?.type === "hardware") {
            this.stream = { ...this.stream, ipCamPassword: event.target.value };
        }
    };
    private onChangeHardwareType = (event: ChangeEvent<HTMLSelectElement>) => {
        const hardwareType = event.target.value as HardwareType;

        if (this.stream?.type === "hardware") {
            this.stream = {
                ...this.stream,
                hardwareType,
            };
        }
    };
    private onChangeHardwareLabel = (event: ChangeEvent<InputElement>) => {
        if (this.stream?.type === "hardware") {
            this.stream = { ...this.stream, label: event.target.value };
        }
    };
    private onKeydown = (event: KeyboardEvent<InputElement>) => {
        if (event.key === "Enter") {
            this.onAddNewStream();
        }
    };

    private onAddNewStream = (passedStream?: StreamType) => {
        const {
            document,
            realtimeMode,
            mode,
            onAddRosStream,
            onAddCustomStream,
            onAddHardwareStream,
            specificOnEdit,
            onEdit,
        } = this.props;

        this.setState({ errorMessage: "" });

        if (passedStream !== undefined) {
            this.stream = passedStream;
        }

        const stream = passedStream !== undefined ? passedStream : this.stream;
        const { customInputType } = this.props;

        if (!stream?.label && realtimeMode) {
            this.setState({ errorMessage: "Name cannot be empty" });
            return;
        }

        if (
            stream?.label &&
            realtimeMode &&
            this.existingStreamNames.includes(stream?.label)
        ) {
            this.setState({ errorMessage: "Name already in use" });
            return;
        }

        if (
            this.stream?.type === "ros" &&
            !this.isValidRosTopic() &&
            realtimeMode
        ) {
            this.setState({ errorMessage: "Invalid ROS topic" });
            return;
        }

        this.setState({ errorMessage: "" });

        if (stream === undefined) {
            this.onStopEdit();
            return;
        }

        let name: string;
        let label: string | undefined;
        const streamType = stream.type;
        if (streamType === "ros") {
            const streams =
                (realtimeMode ? document.realtime : document.teleop)
                    ?.rosStreams || [];

            const existingTopic = streams.find(
                _ => _.mode === mode && _.topicName === stream.topicValue,
            );

            if (existingTopic || !stream.topicValue || !stream.topicType) {
                this.onStopEdit();
                return;
            }

            name = stream.topicValue;
            if (realtimeMode) {
                label = stream.label;
            }
            onAddRosStream({
                topicName: name,
                label,
                topicType: stream.topicType,
                mode,
                encodeVideo: stream.encodeVideo,
                quality: stream.quality,
                bitrate: stream.bitrate,
                rosVersion: stream.rosVersion,
                disableAdaptiveQuality: stream.disableAdaptiveQuality,
                ...(customInputType === "bitset" &&
                    mode === "command" && { buttonLabel: stream.buttonLabel }),
            });
        } else if (streamType === "custom") {
            if (!stream.name) {
                this.onStopEdit();
                return;
            }

            const streams =
                (realtimeMode ? document.realtime : document.teleop)
                    ?.customStreams || [];
            const existing = streams.find(
                _ => _.mode === mode && _.name === stream.name,
            );

            const newLabels = new Set<string>(existing?.labels);
            if (stream.label) {
                newLabels.add(stream.label);
            }

            name = stream.name;
            if (realtimeMode) {
                label = stream.label;
            }
            onAddCustomStream({
                name,
                label,
                rtcStreamType: customInputType,
                mode,
                labels: newLabels.size ? [...newLabels] : undefined,
                encodeVideo: stream.encodeVideo,
                quality: stream.quality,
                bitrate: stream.bitrate,
                disableAdaptiveQuality: stream.disableAdaptiveQuality,
            });
        } else if (streamType === "hardware") {
            if (!stream.name) {
                this.onStopEdit();
                return;
            }
            if (stream.hwDescriptor === undefined) {
                return;
            }
            const {
                hwDescriptor,
                hardwareType,
                rtspEncodingNeeded,
                isOnvif,
                ipCamUsername,
                ipCamPassword,
            } = stream;

            name = cleanHardwareName(stream.name, stream.hardwareType);
            if (realtimeMode) {
                label = stream.label;
            }
            onAddHardwareStream({
                name,
                label,
                rtcStreamType: customInputType,
                hwDescriptor,
                hardwareType,
                rtspEncodingNeeded,
                isOnvif,
                ipCamUsername,
                ipCamPassword,
                mode,
                quality: stream.quality,
                bitrate: stream.bitrate,
                overlayClock: stream.overlayClock ?? false,
            });
        } else {
            assertUnreachable(streamType);
        }

        this.onStopEdit();

        if (specificOnEdit !== undefined) {
            specificOnEdit(name, streamType);
        } else {
            onEdit?.();
        }
    };

    private onStartEdit = () => {
        this.editing = true;
    };

    private onAddV2 = (event?: React.MouseEvent<HTMLButtonElement>) => {
        if (!event) {
            return;
        }

        const rect = event.currentTarget.getBoundingClientRect();
        this.addConnectionPosition = {
            top: rect.top + window.scrollY - 40,
            left: rect.right,
        };
        this.addV2 = true;
    };

    private closeModal = () => {
        this.addV2 = false;
    };

    private hasClosed = () => {
        this.stream = undefined;
    };

    private onStopEdit = () => {
        this.editing = false;
    };

    private isValidRosTopic = () => {
        const stream = this.stream;
        if (stream && (stream as any).topicValue) {
            const rosTopic = (stream as any).topicValue;
            return (
                rosTopic !== undefined &&
                rosTopic.startsWith("/") &&
                rosTopic.length > 1
            );
        }

        return false;
    };

    private getRosVersionLabel(version: string): string {
        switch (version) {
            case "ROS-1":
                return "ROS 1";
            case "ROS-2":
                return "ROS 2";
        }
        return version;
    }
}
